import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  AlertCircle, 
  CheckCircle, 
  Info, 
  X, 
  AlertTriangle 
} from 'lucide-react';

// Notification component
export function Notification({ 
  id,
  title,
  message,
  type = 'info',
  duration = 5000,
  onClose
}) {
  const [progress, setProgress] = useState(0);
  const [intervalId, setIntervalId] = useState(null);
  
  // Setup progress bar
  useEffect(() => {
    const id = setInterval(() => {
      setProgress(prev => {
        if (prev < 100) {
          return prev + (100 / (duration / 100));
        }
        return 100;
      });
    }, 100);
    
    setIntervalId(id);
    
    return () => clearInterval(id);
  }, [duration]);
  
  useEffect(() => {
    if (progress >= 100) {
      clearInterval(intervalId);
      onClose(id);
    }
  }, [progress, intervalId, onClose, id]);
  
  // Get icon based on notification type
  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'error':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'info':
      default:
        return <Info className="h-5 w-5 text-blue-500" />;
    }
  };
  
  // Get background color based on notification type
  const getBgColor = () => {
    switch (type) {
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'info':
      default:
        return 'bg-blue-50 border-blue-200';
    }
  };
  
  // Get progress bar color based on notification type
  const getProgressColor = () => {
    switch (type) {
      case 'success':
        return 'bg-green-500';
      case 'error':
        return 'bg-red-500';
      case 'warning':
        return 'bg-yellow-500';
      case 'info':
      default:
        return 'bg-blue-500';
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: -50, scale: 0.3 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.5, transition: { duration: 0.2 } }}
      className={`${getBgColor()} border shadow-lg rounded-lg p-4 mb-3 max-w-md w-full`}
    >
      <div className="flex items-start">
        <div className="flex-shrink-0">
          {getIcon()}
        </div>
        
        <div className="ml-3 w-0 flex-1 pt-0.5">
          <p className="text-sm font-medium text-gray-900">
            {title}
          </p>
          <p className="mt-1 text-sm text-gray-500">
            {message}
          </p>
        </div>
        
        <div className="ml-4 flex-shrink-0 flex">
          <button
            className="bg-transparent rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none"
            onClick={() => onClose(id)}
          >
            <span className="sr-only">Close</span>
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
      
      {/* Progress bar */}
      <div className="w-full bg-gray-200 h-1 mt-3 rounded-full overflow-hidden">
        <motion.div
          className={`h-full ${getProgressColor()}`}
          initial={{ width: '0%' }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.1, ease: 'linear' }}
        />
      </div>
    </motion.div>
  );
}

// Notification container
export function NotificationContainer({ notifications, onClose }) {
  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col items-end space-y-2 max-h-screen overflow-hidden pointer-events-none">
      <AnimatePresence>
        {notifications.map(notification => (
          <div key={notification.id} className="pointer-events-auto">
            <Notification
              id={notification.id}
              title={notification.title}
              message={notification.message}
              type={notification.type}
              duration={notification.duration}
              onClose={onClose}
            />
          </div>
        ))}
      </AnimatePresence>
    </div>
  );
}